import {
  ActionIcon,
  Button,
  FileButton,
  Group,
  Loader,
  Modal,
  Text,
  TextInput,
} from "@mantine/core";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  ArrowUpDown,
  Check,
  Eye,
  Pause,
  Play,
  Folder,
  ImagePlus,
  ListFilter,
  Search,
} from "lucide-react";
import { useId, useRef, useState, type ReactNode } from "react";

import { getListAssetsQueryKey, listAssets } from "../../api/generated/endpoints/assets/assets";
import type { Asset } from "../../api/generated/model";
import { ErrorAlert } from "../ErrorAlert";
import { MediaMime } from "../../api/generated/zod";
import { IMAGE_TYPES, pictureOf, type PickedImage } from "./ImagePicker";
import { UploadState, useImageUpload } from "./image-upload";
import { previewItem } from "./LibraryPreview";
import { MediaPreview } from "./MediaPreview";
import { clock } from "../../ui/AudioPlayer";
import { Soon } from "../../ui/Soon";
import { scrubTime } from "../../ui/VideoScrubber";
import { LoadAhead, morePages } from "../../ui/LoadAhead";
import classes from "./ReferencesDialog.module.css";

/** Live's page in every library dialog (`ASSETS_DEFAULT_LIMIT`). */
const PAGE_SIZE = 24;

/** What the dialog lists and takes: the brand's images, or its videos (#260). */
export type DialogKind = "image" | "video" | "audio";

/** The types an upload of each kind takes. */
const TYPES: Record<DialogKind, readonly string[]> = {
  image: IMAGE_TYPES,
  video: MediaMime.options.filter((mime) => mime.startsWith("video/")),
  audio: MediaMime.options.filter((mime) => mime.startsWith("audio/")),
};

/** A picture for an asset: an image's own; a video's poster, else none. */
function previewOf(asset: Asset): string | null {
  if (asset.kind === "audio") return null;
  return asset.kind === "video" ? (asset.urls.poster ?? asset.urls.thumb ?? null) : pictureOf(asset);
}

function pickedOf(asset: Asset): PickedImage {
  return {
    assetId: asset.id,
    url: previewOf(asset),
    original: asset.urls.original,
    name: asset.name,
    width: asset.width,
    height: asset.height,
    durationMs: asset.duration_ms,
    bytes: asset.bytes,
  };
}

interface ReferencesDialogProps {
  teamId: string;
  brandId: string;
  /** Live's titles: "Image References", "Select Frame". */
  title: string;
  /** "You can select up to 14 references", "Choose an image for this frame"; none for a video. */
  subtitle?: string;
  /** The brand's images (the default) or its videos. */
  kind?: DialogKind;
  /** How many may be picked: one (a frame, a cap of one) or several (live's references). */
  max: number;
  /** The library images the draft holds, chosen when the dialog opens. */
  chosen: string[];
  /**
   * Those images as the draft holds them, kept on Confirm while still chosen even when no page
   * read so far lists them (a clone's older file, a product's photo: the keyframes', #1263).
   */
  held?: PickedImage[] | undefined;
  onClose: () => void;
  /** The picks, in the order they were made. One pick is only sent when there is one. */
  onConfirm: (picked: PickedImage[]) => void;
}

/**
 * Live's "Image References" and "Select Frame" dialog (G1 decision 23): the brand's library images
 * in a near full-screen grid of portrait tiles, "Upload media", and "Confirm". One pick is a
 * radio, sent on Confirm or closing when none is; several (#233) are checkboxes up to `max`,
 * where a pick past it takes the place of the last one, without a word, as live's. Live's search,
 * filters, folders, sort and "Upload as" are Coming soon: the list route takes none of them yet.
 * A video's eye (live's `reference-videos-modal`, "Select Video") opens it in the shared lightbox
 * over the dialog (issue 828, bug 25, Daniil's request beyond live's in-dialog player), stepping
 * through the listed videos, with live's Confirm, which takes the one shown.
 */
export function ReferencesDialog(props: ReferencesDialogProps) {
  const { teamId, brandId, title, subtitle, max, chosen, onClose, onConfirm } = props;
  const kind = props.kind ?? "image";
  // The brand's media of the kind, newest first, a page at a time, as the library reads them.
  const PARAMS = { kind, trash: false };
  // "audio" has no plural of its own.
  const files = kind === "audio" ? "audio" : `${kind}s`;
  const several = max > 1;
  const [selected, setSelected] = useState<string[]>(chosen);
  // Live's pick: a radio's replaces; a checkbox toggles, and past the cap replaces the last.
  const pick = (id: string) =>
    setSelected((current) => {
      if (!several) return [id];
      if (current.includes(id)) return current.filter((each) => each !== id);
      return current.length < max ? [...current, id] : [...current.slice(0, max - 1), id];
    });
  // A new upload is chosen before the list read again shows it.
  const [uploaded, setUploaded] = useState<Asset | null>(null);
  // The video the lightbox shows, by id; null while it is closed.
  const [previewing, setPreviewing] = useState<string | null>(null);
  const running = useRef<AbortController | null>(null);
  const group = useId();
  const images = useInfiniteQuery({
    // The library's own key for these pages, so both read one cache.
    queryKey: [...getListAssetsQueryKey(teamId, brandId, PARAMS), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listAssets(
        teamId,
        brandId,
        { ...PARAMS, limit: PAGE_SIZE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const upload = useImageUpload({
    teamId,
    brandId,
    kind,
    onUploaded: (asset) => {
      setUploaded(asset);
      pick(asset.id);
    },
    onUploading: (controller) => {
      running.current = controller;
    },
  });
  const close = () => {
    running.current?.abort();
    onClose();
  };

  const listed = images.data?.pages.flatMap((page) => page.items) ?? [];
  const items =
    uploaded === null || listed.some((asset) => asset.id === uploaded.id)
      ? listed
      : [uploaded, ...listed];
  const choices = selected.flatMap((id) => {
    const asset = items.find((each) => each.id === id);
    if (asset !== undefined) return [pickedOf(asset)];
    const kept = props.held?.find((each) => each.assetId === id);
    return kept === undefined ? [] : [kept];
  });
  // As live: Confirm is there before a pick; one pick with nothing picked only closes, several
  // send what is picked, none included.
  const confirm = () => {
    if (several || choices.length > 0) onConfirm(choices);
    onClose();
  };

  return (
    <Modal.Root
      opened
      onClose={close}
      // While its lightbox is open, Escape is the lightbox's alone, as Add Actors'.
      closeOnEscape={previewing === null}
      centered
      classNames={{ content: classes.content }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header className={classes.header}>
          <div>
            <Modal.Title className={classes.title}>{title}</Modal.Title>
            {subtitle === undefined ? null : <Text className={classes.subtitle}>{subtitle}</Text>}
          </div>
          <Modal.CloseButton aria-label="Close" />
        </Modal.Header>
        <Modal.Body className={classes.body}>
          <div className={classes.toolbar}>
            <Group gap={8} wrap="nowrap">
              <Soon>
                {(soon) => (
                  <TextInput
                    {...soon}
                    readOnly
                    className={classes.search}
                    aria-label="Search media files"
                    placeholder="Search media files"
                    leftSection={<Search size={16} aria-hidden />}
                    rightSection={<ArrowRight size={14} aria-hidden />}
                  />
                )}
              </Soon>
              <SoonIcon label="Filter" icon={<ListFilter size={18} />} bordered />
            </Group>
            <Group gap={8} wrap="nowrap">
              <SoonIcon label="Folders" icon={<Folder size={18} />} />
              <SoonIcon label="Sort" icon={<ArrowUpDown size={18} />} />
              <FileButton
                accept={TYPES[kind].join(",")}
                onChange={(file) => void upload.send(file)}
                inputProps={{ "aria-label": `Choose ${kind === "video" ? "a video" : "an image"} to upload` }}
              >
                {(buttonProps) => (
                  <Button
                    {...buttonProps}
                    variant="outline"
                    leftSection={<ImagePlus size={16} />}
                    loading={upload.pending}
                  >
                    Upload media
                  </Button>
                )}
              </FileButton>
              <Soon>{(soon) => <Button {...soon} variant="outline">Upload as</Button>}</Soon>
            </Group>
          </div>
          <UploadState upload={upload} />
          <div className={classes.scroll}>
            {images.isPending ? (
              <Loader className={classes.loader} aria-label={`Loading ${files}`} />
            ) : null}
            {images.isError ? <ErrorAlert error={images.error} /> : null}
            {images.isSuccess && items.length === 0 ? (
              <Text className={classes.empty}>
                No {kind}s in this library yet. Upload one.
              </Text>
            ) : null}
            <div
              className={kind === "audio" ? classes.rows : classes.grid}
              role={several ? "group" : "radiogroup"}
              aria-label={title}
            >
              {items.map((asset) =>
                kind === "audio" ? (
                  <AudioRow
                    key={asset.id}
                    asset={asset}
                    group={several ? undefined : group}
                    checked={selected.includes(asset.id)}
                    onSelect={() => pick(asset.id)}
                  />
                ) : (
                  <Tile
                    key={asset.id}
                    asset={asset}
                    group={several ? undefined : group}
                    checked={selected.includes(asset.id)}
                    onSelect={() => pick(asset.id)}
                    onPreview={kind === "video" ? () => setPreviewing(asset.id) : undefined}
                  />
                ),
              )}
            </div>
            {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
            <LoadAhead more={morePages(images)} loaded={items.length} label={`Loading more ${files}`} />
          </div>
          <Group justify="center" gap={12} className={classes.footer}>
            {/* Not while an upload runs: its image is the one about to be chosen. */}
            {/* Live's audio list (capture 15) and video list (templates-video 58) hold Confirm until
                a pick; its image dialog does not. */}
            <Button
              onClick={confirm}
              disabled={upload.pending || (kind !== "image" && choices.length === 0)}
            >
              Confirm
            </Button>
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
          </Group>
        </Modal.Body>
      </Modal.Content>
      {kind === "video" ? (
        <MediaPreview
          items={items.map(previewItem)}
          current={previewing}
          onShow={setPreviewing}
          more={morePages(images)}
          actions={(item) => {
            const asset = items.find((each) => each.id === item.id);
            // Live's preview "Confirm": the dialog takes the video shown, and closes.
            return asset === undefined ? null : (
              <Button
                size="xs"
                onClick={() => {
                  onConfirm([pickedOf(asset)]);
                  onClose();
                }}
              >
                Confirm
              </Button>
            );
          }}
        />
      ) : null}
    </Modal.Root>
  );
}

interface TileProps {
  asset: Asset;
  /** The radio group's name for one pick; none for several, which are checkboxes. */
  group: string | undefined;
  checked: boolean;
  onSelect: () => void;
  /** A video's eye (live's), which opens it in the lightbox; none for an image or a sound. */
  onPreview?: (() => void) | undefined;
}

/** An image to choose, as live's: a portrait tile, a gradient ring and check once chosen. */
function Tile({ asset, group, checked, onSelect, onPreview }: TileProps) {
  const url = previewOf(asset);
  const [failed, setFailed] = useState(false);
  return (
    <label className={classes.tile} data-checked={checked || undefined}>
      <input
        type={group === undefined ? "checkbox" : "radio"}
        name={group}
        className={classes.input}
        aria-label={asset.name}
        checked={checked}
        onChange={onSelect}
      />
      {url === null || failed ? (
        <span className={classes.placeholder} aria-hidden>
          {asset.name}
        </span>
      ) : (
        <img src={url} alt="" className={classes.picture} onError={() => setFailed(true)} />
      )}
      {checked ? (
        <span className={classes.check} aria-hidden>
          <Check size={16} strokeWidth={3} />
        </span>
      ) : null}
      {asset.kind === "video" && asset.duration_ms !== null ? (
        // Live's length on each clip (templates-video 58): a form's limit is by it (Actor Swap's 15 s).
        <span className={classes.length}>{scrubTime(asset.duration_ms / 1000)}</span>
      ) : null}
      {onPreview === undefined ? null : (
        <ActionIcon
          className={classes.eye}
          variant="filled"
          aria-label={`Preview ${asset.name}`}
          onClick={(event) => {
            // The eye opens the video, and leaves the pick as it is.
            event.preventDefault();
            onPreview();
          }}
        >
          <Eye size={16} aria-hidden />
        </ActionIcon>
      )}
    </label>
  );
}

/** One of live's toolbar icons that the list route does not serve yet. */
function SoonIcon(props: { label: string; icon: ReactNode; bordered?: boolean }) {
  return (
    <Soon>
      {(soon) => (
        <ActionIcon
          {...soon}
          variant={props.bordered === true ? "default" : "subtle"}
          size={36}
          aria-label={props.label}
        >
          {props.icon}
        </ActionIcon>
      )}
    </Soon>
  );
}

/**
 * An audio to choose, as live's (capture 15): a row with a round play button, "00:00 / 00:10"
 * and the file's name; chosen, a ring.
 */
function AudioRow({ asset, group, checked, onSelect }: TileProps) {
  const player = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const toggle = () => {
    const element = player.current;
    if (element === null) return;
    if (element.paused) void element.play().catch(() => setPlaying(false));
    else element.pause();
  };
  return (
    <label className={classes.row} data-checked={checked || undefined}>
      <input
        type={group === undefined ? "checkbox" : "radio"}
        name={group}
        className={classes.input}
        aria-label={asset.name}
        checked={checked}
        onChange={onSelect}
      />
      <ActionIcon
        variant="light"
        radius="xl"
        size={28}
        aria-label={playing ? `Pause ${asset.name}` : `Play ${asset.name}`}
        onClick={(event) => {
          event.preventDefault();
          toggle();
        }}
      >
        {playing ? <Pause size={14} aria-hidden /> : <Play size={14} aria-hidden />}
      </ActionIcon>
      <span className={classes.rowTime}>
        {clock(at)} / {clock((asset.duration_ms ?? 0) / 1000)}
      </span>
      <span className={classes.rowName}>{asset.name}</span>
      {asset.urls.original === null ? null : (
        // A preview to listen to; it has no words to caption.
        // oxlint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={player}
          src={asset.urls.original}
          preload="none"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onTimeUpdate={(event) => setAt(event.currentTarget.currentTime)}
        />
      )}
    </label>
  );
}
